/**
 * Toolbar sort choices for both tracker boards.
 *
 * The briefs board sorts by fields every brief has (created date, title), so
 * its list is fixed. A custom tracker adds to those the tracker's own sortable
 * columns — which is why "Due date" or "Status" appear on one tracker and not
 * another.
 *
 * `null` (Default) keeps the user's drag order; any other choice overrides it
 * until Default is picked again. `lib/sorting.ts` turns an `id` back into the
 * comparator.
 */
import type { ReactNode } from 'react';
import {
  ArrowDown10Icon,
  ArrowDownAZIcon,
  ArrowDownZAIcon,
  Building2Icon,
  CalendarArrowDownIcon,
  CalendarArrowUpIcon,
  CalendarClockIcon,
  CircleDotIcon,
  LinkIcon,
  PercentIcon,
  SquareCheckIcon,
} from 'lucide-react';

import { i18n } from '@/shared/i18n';
import type { SortChipOption } from '@/shared/ui';
import { isRecordSortable, type RecordField } from './record-fields';
import { COLUMN_SORT_PREFIX, isSortableColumnType } from './sorting';
import type { CustomColumnType } from './useTrackersBoard';

/**
 * The briefs board's fixed rows.
 *
 * Labels are getters so each read resolves against the active locale; a plain
 * `i18n.t(...)` here would freeze the English string at import time.
 */
export function briefSortChoices(): ReadonlyArray<SortChipOption> {
  return [
    {
      id: 'createdAt-desc',
      get label() {
        return i18n.t('trackers:trackersBoard.sortChoices.newestFirst');
      },
      direction: 'desc',
      icon: <CalendarArrowDownIcon className="size-4" />,
    },
    {
      id: 'createdAt-asc',
      get label() {
        return i18n.t('trackers:trackersBoard.sortChoices.oldestFirst');
      },
      direction: 'asc',
      icon: <CalendarArrowUpIcon className="size-4" />,
    },
    {
      id: 'title-asc',
      get label() {
        return i18n.t('trackers:trackersBoard.sortChoices.nameAZ');
      },
      direction: 'asc',
      icon: <ArrowDownAZIcon className="size-4" />,
    },
    {
      id: 'title-desc',
      get label() {
        return i18n.t('trackers:trackersBoard.sortChoices.nameZA');
      },
      direction: 'desc',
      icon: <ArrowDownZAIcon className="size-4" />,
    },
  ];
}

const COLUMN_SORT_ICONS: Partial<Record<CustomColumnType, ReactNode>> = {
  checkbox: <SquareCheckIcon className="size-4" />,
  date: <CalendarClockIcon className="size-4" />,
  date_range: <CalendarClockIcon className="size-4" />,
  link: <LinkIcon className="size-4" />,
  number: <ArrowDown10Icon className="size-4" />,
  progress: <PercentIcon className="size-4" />,
  status: <CircleDotIcon className="size-4" />,
  team: <Building2Icon className="size-4" />,
  text: <ArrowDownAZIcon className="size-4" />,
};

interface SortableColumnMeta {
  id: string;
  label: string;
  type?: CustomColumnType;
}

/**
 * Item name and created date, then one row per sortable column, in the board's
 * column order. Every generated row sorts ascending — soonest date, lowest
 * number, first status — so the label can name the column instead of a
 * direction.
 *
 * The two date rows are not in the design's option set, but a custom row has a
 * created date exactly as a brief does, and dropping them here alone would
 * cost "Newest first" to anyone moving between the two boards.
 */
export function buildTrackerSortChoices(
  columns: ReadonlyArray<SortableColumnMeta>,
): ReadonlyArray<SortChipOption> {
  return [
    {
      id: 'title-asc',
      get label() {
        return i18n.t('trackers:trackersBoard.sortChoices.itemAZ');
      },
      direction: 'asc',
      icon: <ArrowDownAZIcon className="size-4" />,
    },
    {
      id: 'createdAt-desc',
      get label() {
        return i18n.t('trackers:trackersBoard.sortChoices.newestFirst');
      },
      direction: 'desc',
      icon: <CalendarArrowDownIcon className="size-4" />,
    },
    {
      id: 'createdAt-asc',
      get label() {
        return i18n.t('trackers:trackersBoard.sortChoices.oldestFirst');
      },
      direction: 'asc',
      icon: <CalendarArrowUpIcon className="size-4" />,
    },
    ...columns
      .filter((column) => isSortableColumnType(column.type))
      .map((column) => ({
        id: `${COLUMN_SORT_PREFIX}${column.id}`,
        get label() {
          return column.type === 'date' || column.type === 'date_range'
            ? i18n.t('trackers:trackersBoard.sortChoices.columnSoonest', { name: column.label })
            : column.label;
        },
        direction: 'asc' as const,
        icon: COLUMN_SORT_ICONS[column.type ?? 'text'],
      })),
  ];
}

/** The field a record board leads its sort list with, when its source has one. */
const CREATED_AT_FIELD = 'createdAt';

/**
 * A record board's rows: one per field its source declares a sort for.
 *
 * The ids are bare `<fieldId>-<direction>`, unlike the client boards'
 * `column:`-prefixed ones. `useRecordBoard` splits an id at its last hyphen and
 * sends the head to the server as `sortBy`, so a prefix would travel as part of
 * the field name and come back a 400.
 *
 * Created date leads with both directions, because newest-first is the order
 * people arrive expecting. Everything else offers ascending only - soonest
 * date, lowest number, first status - so the label can name the field instead
 * of a direction.
 */
export function buildRecordSortChoices(
  fields: readonly RecordField[],
  labelOf: (field: RecordField) => string,
): ReadonlyArray<SortChipOption> {
  const sortable = fields.filter(isRecordSortable);
  const created = sortable.find((field) => field.id === CREATED_AT_FIELD);

  return [
    ...(created
      ? [
          {
            id: `${created.id}-desc`,
            get label() {
              return i18n.t('trackers:trackersBoard.sortChoices.newestFirst');
            },
            // eslint-disable-next-line i18next/no-literal-string -- a sort direction the board acts on, never rendered
            direction: 'desc' as const,
            icon: <CalendarArrowDownIcon className="size-4" />,
          },
          {
            id: `${created.id}-asc`,
            get label() {
              return i18n.t('trackers:trackersBoard.sortChoices.oldestFirst');
            },
            // eslint-disable-next-line i18next/no-literal-string -- a sort direction the board acts on, never rendered
            direction: 'asc' as const,
            icon: <CalendarArrowUpIcon className="size-4" />,
          },
        ]
      : []),
    ...sortable
      .filter((field) => field !== created)
      .map((field) => ({
        id: `${field.id}-asc`,
        get label() {
          const name = labelOf(field);
          return field.type === 'date'
            ? i18n.t('trackers:trackersBoard.sortChoices.columnSoonest', { name })
            : name;
        },
        direction: 'asc' as const,
        icon: COLUMN_SORT_ICONS[field.type] ?? COLUMN_SORT_ICONS.text,
      })),
  ];
}
